import React, { useState, useContext, useEffect } from 'react';
import IntroductionContext from '../context/IntroductionContext';

const EditHomePageModal = ({ isOpen, onClose }) => {
    const { introduction, updateIntroduction, uploadIntroductionImages } = useContext(IntroductionContext);
    const [formData, setFormData] = useState({
        englishTitle: '',
        persianTitle: '',
        englishIntroduction: '',
        persianIntroduction: '',
        leftImage: null,
        rightImage: null,
        contactUs: '',
        englishAboutUs: '',
        persianAboutUs: ''
    });
    const [loading, setLoading] = useState(false);

    // Load existing data when modal opens
    useEffect(() => {
        if (isOpen && introduction) {
            setFormData({
                englishTitle: introduction.english_title || '',
                persianTitle: introduction.persian_title || '',
                englishIntroduction: introduction.english_introduction || '',
                persianIntroduction: introduction.persian_introduction || '',
                leftImage: null,
                rightImage: null,
                contactUs: introduction.contact_us || '',
                englishAboutUs: introduction.english_about_us || '',
                persianAboutUs: introduction.persian_about_us || ''
            });
        }
    }, [isOpen, introduction]);

    const handleInputChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === 'file') {
            setFormData(prev => ({
                ...prev,
                [name]: files[0]
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // First, upload images if any
            if (formData.leftImage || formData.rightImage) {
                const uploadResult = await uploadIntroductionImages(formData.leftImage, formData.rightImage);
                if (!uploadResult.success) {
                    alert(`Failed to upload images: ${uploadResult.error}`);
                    setLoading(false);
                    return;
                }
            }

            // Then update the introduction content
            const updateResult = await updateIntroduction({
                english_title: formData.englishTitle,
                persian_title: formData.persianTitle,
                english_introduction: formData.englishIntroduction,
                persian_introduction: formData.persianIntroduction,
                contact_us: formData.contactUs,
                english_about_us: formData.englishAboutUs,
                persian_about_us: formData.persianAboutUs
            });

            if (updateResult.success) {
                alert('Home page updated successfully!');
                onClose();
            } else {
                alert(`Failed to update home page: ${updateResult.error}`);
            }
        } catch (error) {
            console.error('Error updating home page:', error);
            alert('An error occurred while updating the home page');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setFormData({
            englishTitle: '',
            persianTitle: '',
            englishIntroduction: '',
            persianIntroduction: '',
            leftImage: null,
            rightImage: null,
            contactUs: '',
            englishAboutUs: '',
            persianAboutUs: ''
        });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="edit-home-modal-overlay" onClick={handleCancel}></div>
            <div className="edit-home-modal">
                <div className="edit-home-modal__header">
                    <h2>Edit Home Page</h2>
                    <button className="edit-home-modal__close" onClick={handleCancel}>×</button>
                </div>
                <div className="edit-home-modal__content">
                    <form onSubmit={handleSubmit}>
                        <div className="edit-home-modal__form-group">
                            <label htmlFor="englishTitle">English Title</label>
                            <input
                                type="text"
                                id="englishTitle"
                                name="englishTitle"
                                value={formData.englishTitle}
                                onChange={handleInputChange}
                                placeholder="Enter English title"
                                required
                            />
                        </div>

                        <div className="edit-home-modal__form-group">
                            <label htmlFor="persianTitle">Persian Title</label>
                            <input
                                type="text"
                                id="persianTitle"
                                name="persianTitle"
                                value={formData.persianTitle}
                                onChange={handleInputChange}
                                placeholder="عنوان فارسی را وارد کنید"
                                dir="rtl"
                            />
                        </div>

                        <div className="edit-home-modal__form-group">
                            <label htmlFor="englishIntroduction">English Introduction</label>
                            <textarea
                                id="englishIntroduction"
                                name="englishIntroduction"
                                value={formData.englishIntroduction}
                                onChange={handleInputChange}
                                placeholder="Enter English introduction content"
                                rows="6"
                                required
                            />
                        </div>

                        <div className="edit-home-modal__form-group">
                            <label htmlFor="persianIntroduction">Persian Introduction</label>
                            <textarea
                                id="persianIntroduction"
                                name="persianIntroduction"
                                value={formData.persianIntroduction}
                                onChange={handleInputChange}
                                placeholder="محتوی معرفی به فارسی را وارد کنید"
                                dir="rtl"
                                rows="6"
                            />
                        </div>

                        <div className="edit-home-modal__form-group">
                            <label htmlFor="leftImage">Left Image</label>
                            <input
                                type="file"
                                id="leftImage"
                                name="leftImage"
                                accept="image/*"
                                onChange={handleInputChange}
                            />
                        </div>

                        <div className="edit-home-modal__form-group">
                            <label htmlFor="rightImage">Right Image</label>
                            <input
                                type="file"
                                id="rightImage"
                                name="rightImage"
                                accept="image/*"
                                onChange={handleInputChange}
                            />
                        </div>

                        <div className="edit-home-modal__form-group">
                            <label htmlFor="contactUs">Contact Us</label>
                            <textarea
                                id="contactUs"
                                name="contactUs"
                                value={formData.contactUs}
                                onChange={handleInputChange}
                                placeholder="Enter contact information"
                                rows="3"
                            />
                        </div>

                        <div className="edit-home-modal__form-group">
                            <label htmlFor="englishAboutUs">English About Us</label>
                            <textarea
                                id="englishAboutUs"
                                name="englishAboutUs"
                                value={formData.englishAboutUs}
                                onChange={handleInputChange}
                                placeholder="Enter English about us content"
                                rows="4"
                            />
                        </div>

                        <div className="edit-home-modal__form-group">
                            <label htmlFor="persianAboutUs">Persian About Us</label>
                            <textarea
                                id="persianAboutUs"
                                name="persianAboutUs"
                                value={formData.persianAboutUs}
                                onChange={handleInputChange}
                                placeholder="محتوی درباره ما به فارسی را وارد کنید"
                                dir="rtl"
                                rows="4"
                            />
                        </div>

                        <div className="edit-home-modal__form-actions">
                            <button 
                                type="button" 
                                className="edit-home-modal__button edit-home-modal__button--cancel"
                                onClick={handleCancel}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="edit-home-modal__button edit-home-modal__button--submit"
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default EditHomePageModal; 